import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { JSX } from "react";
import {
  ModuleLayout,
  useListSelection,
  useSearchFilter,
} from "../../layout/ModuleLayout";
import {
  Icon,
  IconButton,
  IconRoundBadge,
  ModuleListItem,
  TopBarHeader,
} from "../../components/ui";
import { NeedsReplyBell } from "./NeedsReplyBell";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { ListPane } from "../../components/layout/ListPane";
import { DetailPane } from "../../components/layout/DetailPane";
import { DateSeparator } from "../../components/ui/DateSeparator";
import { AgentChatItem } from "./AgentChatItem";
import { AgentPanel } from "../../agent/AgentPanel";
import { ContextPanel, AgentStatsRegionConnected } from "../../panels";
import { useAgentData } from "./hooks/useAgentData";
import { buildEpisodesTimeline, buildContextPanelProps, filterChatTimeline, getTimelineItemById } from "./helpers";
import { useAgentModuleStore } from "./store";
import { useCreateEpisodeMutation, useEpisodeDetailQuery, useArchiveEpisodeMutation, useUnarchiveEpisodeMutation } from "./queries";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useRouterContext } from "../../providers/RouterProvider";
import { useUiStore } from "../../store/uiStore";
import { groupByDate } from "../../components/ui/groupHelpers";
import {
  effectiveSelectionId,
  getEpisodeRouteSelection,
  normalizeEpisodeSelectionId,
  shouldSyncEpisodeSelectionFromRoute,
} from "./routeSelection";

export function AgentModule(): JSX.Element {
  const runtime = useAppRuntime();
  const router = useRouterContext();
  const data = useAgentData();
  const timeline = useMemo(() => buildEpisodesTimeline(data), [data]);
  const createEpisodeMutation = useCreateEpisodeMutation();

  const storeSelectedId = useAgentModuleStore((s) => s.selectedEpisodeId);
  const searchQuery = useAgentModuleStore((s) => s.searchQuery);
  const listFilter = useAgentModuleStore((s) => s.listFilter);
  const { setSelectedEpisodeId: setStoreSelectedId, setSearchQuery, setListFilter } = useAgentModuleStore((s) => s.actions);

  // ── URL sync: #/episodes/episode/{uuid} ──
  // Timeline IDs are "agent:{uuid}", URL stores raw uuid
  const selectedId = storeSelectedId;
  const isNewEpisodeRoute =
    router.entityType === "new" && router.entityId === undefined;

  const setSelectedId = useCallback((id: string | undefined) => {
    // Deep links (bell dropdown / search drill-in) deliver RAW uuids via
    // router.pendingItemId — normalize to the "agent:<uuid>" timeline id.
    const normalized = normalizeEpisodeSelectionId(id);
    setStoreSelectedId(normalized);
    // Sync to URL: strip "agent:" prefix for clean URLs
    const rawId = normalized?.startsWith("agent:") ? normalized.slice(6) : normalized;
    router.setSelection("episode", rawId);
  }, [setStoreSelectedId, router]);

  // Keep module selection in sync with manual/direct URL changes.
  useEffect(() => {
    const routeSelectedId = getEpisodeRouteSelection(router.entityType, router.entityId);
    if (shouldSyncEpisodeSelectionFromRoute(routeSelectedId, storeSelectedId)) {
      setStoreSelectedId(routeSelectedId);
    }
  }, [router.entityId, router.entityType, storeSelectedId, setStoreSelectedId]);

  // Feed the pending deep link (bell dropdown / search drill-in) as the
  // effective selection so the default-select-first effect can't
  // overwrite it on a fresh mount. @tested-by: tst_fe_chats_nav_002
  useListSelection(
    isNewEpisodeRoute ? [] : timeline,
    effectiveSelectionId(selectedId, router.pendingItemId),
    setSelectedId,
  );

  // "Needs reply / All" toggle — needs_input-only scope as a list filter
  // (docs/backend/episode-status.md).
  const visibleTimeline = useMemo(
    () => filterChatTimeline(timeline, listFilter),
    [timeline, listFilter],
  );

  const filtered = useSearchFilter(visibleTimeline, searchQuery, (e, q) =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    e.title.toLowerCase().includes(q) || (e.preview?.toLowerCase().includes(q) ?? false),
  );

  const chat = selectedId ? getTimelineItemById(timeline, selectedId) : undefined;
  const episodeId = chat?.id.startsWith("agent:") ? chat.id.slice(6) : undefined;
  const { data: episodeDetail } = useEpisodeDetailQuery(episodeId);
  const archiveMutation = useArchiveEpisodeMutation();
  const unarchiveMutation = useUnarchiveEpisodeMutation();
  const groupedTimeline = useMemo(
    () => groupByDate(filtered, (item) => item.activityAt),
    [filtered],
  );

  // Handle pending agent search from CommandBar
  const pendingAgentQuery = useUiStore((s) => s.pendingAgentQuery);
  const pendingAgentMentionIds = useUiStore((s) => s.pendingAgentMentionIds);
  // Tracks contextKey for auto-selecting newly created episodes (search or "New" button)
  const pendingContextRef = useRef<string | null>(null);
  const initializedNewRouteRef = useRef(false);

  const createAndOpenEpisode = useCallback(() => {
    const clientId = crypto.randomUUID();
    pendingContextRef.current = clientId;
    void runtime.agent.chat.startNewEpisode(clientId, clientId);
    createEpisodeMutation.mutate({ title: "New chat", client_id: clientId });
    setSelectedId(`agent:${clientId}`);
  }, [createEpisodeMutation, runtime, setSelectedId]);

  useLayoutEffect(() => {
    if (!isNewEpisodeRoute) {
      initializedNewRouteRef.current = false;
      return;
    }
    if (initializedNewRouteRef.current) return;
    initializedNewRouteRef.current = true;
    // @tested-by: tst_fe_scn_cloud_demo_040, tst_fe_scn_cloud_demo_044
    // @invariant: demo entry creates a normal selected episode. Starter cards
    // are its empty state; they never replace the standard three-panel layout.
    createAndOpenEpisode();
  }, [createAndOpenEpisode, isNewEpisodeRoute]);

  useEffect(() => {
    if (!pendingAgentQuery) return;
    const query = pendingAgentQuery;
    const mentionIds = [...pendingAgentMentionIds];
    useUiStore.getState().setPendingAgentQuery(null);
    useUiStore.getState().setPendingAgentMentionIds([]);
    // Plan item #6: ephemeral (search) surface uses a one-shot client_id as
    // routing key — no correlation across tabs.
    const clientId = crypto.randomUUID();
    const contextKey = clientId;
    pendingContextRef.current = clientId;
    const chat = runtime.agent.chat;
    // @tested-by: tst_fe_scn_agent_search_001
    const attachments = mentionIds.map((id) => ({
      schemaId: "entity",
      data: { id },
    }));
    // Same optimistic pattern as "New" button: mutation inserts into list cache,
    // presetEpisodeId prevents sendMessage from creating a duplicate.
    createEpisodeMutation.mutate({
      title: query.length > 60 ? query.slice(0, 60) + "..." : query,
      client_id: clientId,
      mentioned_entity_ids: mentionIds.length > 0 ? mentionIds : undefined,
    });
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    void (async () => {
      await chat.startNewEpisode(contextKey, clientId);
      await chat.sendMessage(
        contextKey, query, { activeModule: "search" },
        undefined, undefined,
        attachments.length > 0 ? attachments : undefined,
      );
    })();
  }, [pendingAgentQuery, pendingAgentMentionIds, runtime, createEpisodeMutation]);

  // INV-5: "New" button — same optimistic pattern as notes/projects (useMutation + onMutate)
  const handleNewEpisode = useCallback(() => {
    createAndOpenEpisode();
  }, [createAndOpenEpisode]);

  // Auto-select episode once it appears in the timeline (search or "New" button).
  // Plan #6: pendingContextRef holds the client_id, which equals the optimistic
  // episode id and (after settle) the server-assigned id.
  useEffect(() => {
    if (!pendingContextRef.current) return;
    const pending = pendingContextRef.current;
    const ep = timeline.find((t) => t.id === `agent:${pending}`);
    if (ep && selectedId !== ep.id) {
      setSelectedId(ep.id);
    }
    if (ep) pendingContextRef.current = null;
  }, [timeline, selectedId, setSelectedId]);

  return (
    <ModuleLayout
      moduleName="Chats"
      rightPane={
        chat
          ? (
            <ContextPanel
              {...buildContextPanelProps(chat, episodeDetail?.linked_entities)}
              runtime={runtime}
              bottomSlot={
                <AgentStatsRegionConnected
                  runtime={runtime}
                  contextKey={chat.id.replace("agent:", "")}
                  episodeId={chat.id.replace("agent:", "")}
                />
              }
            />
          )
          : (
            <ContextPanel
              infoItems={[]}
              connectedEntities={[]}
              runtime={runtime}
            />
          )
      }
      listPane={
        <ListPane
          title="Chats"
          count={timeline.length}
          onSearch={setSearchQuery}
          searchAccessory={
            <NeedsReplyBell
              pressed={listFilter === "needs_reply"}
              onToggle={() => {
                setListFilter(listFilter === "needs_reply" ? "all" : "needs_reply");
              }}
            />
          }
          headerActions={
            <button
              type="button"
              onClick={handleNewEpisode}
              title="New chat"
              className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer hover:bg-surface-hover"
            >
              <Icon name="plus" size={16} className="text-content-secondary" />
            </button>
          }
        >
          {groupedTimeline.map((group) => (
            <div key={group.key}>
              {group.date ? <DateSeparator date={group.date} /> : null}
              {group.items.map((e) => {
                const isSelected = e.id === selectedId;
                // Archive is the entity's own flag: it stopped being a
                // status value with episode-storage S1, so reading `status`
                // here answered `false` for every row.
                const isArchived = e.isArchived === true;
                return (
                  <div
                    key={e.id}
                    className="cursor-pointer"
                    onClick={() => { setSelectedId(e.id); }}
                  >
                    <ModuleListItem selected={isSelected}>
                      <AgentChatItem
                        title={e.title}
                        preview={e.preview}
                        time={e.time}
                        iconName={e.iconName}
                        color={e.color}
                        onAction={() => {
                          const id = e.id.replace("agent:", "");
                          if (isArchived) {
                            unarchiveMutation.mutate({ id });
                          } else {
                            archiveMutation.mutate({ id });
                          }
                        }}
                        actionIconName={isArchived ? "archive-restore" : "archive"}
                      />
                    </ModuleListItem>
                  </div>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="py-10 px-4 text-center text-content-tertiary">
              {searchQuery ? "No results found" : "No items"}
            </div>
          )}
        </ListPane>
      }
      detailPane={
        chat ? (
          <DetailPane
            headerNode={
              <TopBarHeader
                leading={
                  <IconRoundBadge
                    iconName={chat.iconName}
                    colorClassName={`bg-${chat.color}`}
                    iconSize={14}
                    className="h-8 w-8"
                  />
                }
                title={chat.title}
                subtitle={
                  <span className="flex items-center gap-1.5">
                    <StatusBadge status={chat.status} />
                    {chat.sourceModuleId === "triggers" && (
                      <span className="text-[10px] rounded-full px-1.5 py-0.5 shrink-0 bg-orange-500/15 text-orange-400">
                        Auto
                      </span>
                    )}
                  </span>
                }
                actions={
                  <IconButton variant="ghost">
                    <Icon name="ellipsis-vertical" size={15} />
                  </IconButton>
                }
              />
            }
          >
            <AgentPanel
              moduleName={chat.targetModuleId}
              conversationKey={chat.id.replace("agent:", "")}
              presetEpisodeId={chat.id.replace("agent:", "")}
              selectedItemId={chat.targetModuleId === "telegram" ? undefined : (chat.targetItemId ?? chat.id)}
              selectedChatId={chat.targetModuleId === "telegram" ? chat.targetItemId : undefined}
              selectedChatName={chat.title}
              contextTitle="Chats"
              archived={chat.isArchived === true}
            />
          </DetailPane>
        ) : (
          <DetailPane>
            <div className="flex items-center justify-center h-full text-content-tertiary text-base">
              No conversations
            </div>
          </DetailPane>
        )
      }
    />
  );
}
